import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import { Archive, CheckCircle2, Circle, Inbox } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "../ui/motion";
import { InboxContextMenu } from "./InboxContextMenu";
import { getNotificationConfig } from "./notificationConfig";
import type { NotificationWithStatus } from "./types";

interface Props {
  notification: NotificationWithStatus;
  isSelected: boolean;
  isArchived: boolean;
  isFocused: boolean;
  onSelect: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onOpenRelated: (id: string) => void;
  index: number;
}

export function InboxNotificationItem({
  notification,
  isSelected,
  isArchived,
  isFocused,
  onSelect,
  onArchive,
  onUnarchive,
  onMarkRead,
  onMarkUnread,
  onOpenRelated,
  index,
}: Props) {
  const { t } = useTranslation("inbox");
  const createdDate = new Date(notification.createdAt);
  const hoursAgo = (Date.now() - notification.createdAt) / (1000 * 60 * 60);
  const timeLabel =
    hoursAgo > 24
      ? `${String(createdDate.getDate()).padStart(2, "0")}.${String(createdDate.getMonth() + 1).padStart(2, "0")}.${createdDate.getFullYear()}`
      : formatDistanceToNow(createdDate, { addSuffix: true });

  const config = getNotificationConfig(notification.category);
  const Icon = config.icon;

  return (
    <InboxContextMenu
      notification={{
        id: notification.id,
        isRead: notification.isRead,
        isArchived,
        relatedEntityId: notification.relatedEntityId,
        relatedEntityType: notification.relatedEntityType,
      }}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onMarkRead={onMarkRead}
      onMarkUnread={onMarkUnread}
      onOpenRelated={onOpenRelated}
    >
      <button
        type="button"
        onClick={() => onSelect(notification.id)}
        className={`
          relative w-full text-left px-3 py-3 flex gap-3 items-start transition-colors
          hover:bg-card-hover/40 group
          ${isSelected ? "bg-accent-soft" : ""}
          ${!notification.isRead && !isSelected ? "bg-card border-l-2 border-l-accent" : "border-l-2 border-l-transparent"}
          ${isFocused ? "ring-1 ring-inset ring-accent/50" : ""}
        `}
      >
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ ...springs.smooth, delay: index * 0.03 }}
          className="flex gap-3 items-start w-full"
        >
          {/* Category icon badge with unread overlay */}
          <div className="relative shrink-0 mt-0.5">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ backgroundColor: config.bgColor }}
            >
              <Icon size={13} style={{ color: config.color }} />
            </div>
            {!notification.isRead && (
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent border-2 border-[var(--color-card)]" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p
              className={`text-[13px] truncate ${
                notification.isRead
                  ? "text-secondary font-normal"
                  : "text-primary font-semibold"
              }`}
            >
              {t(`notificationTitles.${notification.category}`, {
                defaultValue: notification.title,
              })}
            </p>
            <p className="text-[12px] text-secondary truncate mt-0.5">
              {notification.relatedEntityName}
            </p>
            <p className="text-[11px] text-muted mt-1">{timeLabel}</p>
          </div>
        </motion.div>

        {/* Hover actions -- absolutely positioned over the right side of the item */}
        <div
          role="toolbar"
          className={`
            absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1
            opacity-0 group-hover:opacity-100 transition-opacity duration-150
            bg-card-hover/80 backdrop-blur-sm rounded-md px-1 py-0.5
          `}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Toggle read/unread */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (notification.isRead) {
                onMarkUnread(notification.id);
              } else {
                onMarkRead(notification.id);
              }
            }}
            className="p-1 rounded hover:bg-card-hover text-muted hover:text-primary transition-colors"
            title={
              notification.isRead
                ? t("actions.markAsUnread")
                : t("actions.markAsRead")
            }
          >
            {notification.isRead ? (
              <Circle size={13} />
            ) : (
              <CheckCircle2 size={13} />
            )}
          </button>

          {/* Archive / Unarchive */}
          {isArchived ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUnarchive(notification.id);
              }}
              className="p-1 rounded hover:bg-card-hover text-muted hover:text-primary transition-colors"
              title={t("moveToInbox")}
            >
              <Inbox size={13} />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onArchive(notification.id);
              }}
              className="p-1 rounded hover:bg-card-hover text-muted hover:text-primary transition-colors"
              title={t("archive")}
            >
              <Archive size={13} />
            </button>
          )}
        </div>
      </button>
    </InboxContextMenu>
  );
}
